import React, { useEffect, useState } from "react";
import { api, PLATFORM_LABELS, fmtEuro, todayISO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Car, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  date: todayISO(),
  start_time: "",
  end_time: "",
  platform: "uber",
  pickup: "",
  dropoff: "",
  distance_km: 0,
  duration_min: 0,
  gross_amount: 0,
  commission: 0,
  tolls: 0,
  tips: 0,
  notes: "",
  client_id: null,
};

export default function Courses() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, cRes] = await Promise.all([
        api.get("/trips?limit=200"),
        api.get("/clients").catch(() => ({ data: [] })),
      ]);
      setItems(data);
      setClients(cRes.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        distance_km: Number(form.distance_km) || 0,
        duration_min: Number(form.duration_min) || 0,
        gross_amount: Number(form.gross_amount) || 0,
        commission: Number(form.commission) || 0,
        tolls: Number(form.tolls) || 0,
        tips: Number(form.tips) || 0,
      };
      await api.post("/trips", payload);
      toast.success("Course enregistrée");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette course ?")) return;
    await api.delete(`/trips/${id}`);
    toast.success("Course supprimée");
    load();
  };

  const totalCA = items.reduce((s, t) => s + (t.net_amount || 0), 0);
  const totalCount = items.length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Gestion des courses</div>
          <h1 className="font-manrope text-3xl md:text-4xl font-extrabold">Courses</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {totalCount} courses • CA total : <span className="text-foreground font-semibold num">{fmtEuro(totalCA)}</span>
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-add-course" className="active:scale-95 transition-transform">
              <Plus className="h-4 w-4 mr-2" /> Nouvelle course
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-manrope">Enregistrer une course</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Date">
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="input-course-date" />
              </Field>
              <Field label="Plateforme">
                <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v, client_id: v === "prive" ? form.client_id : null })}>
                  <SelectTrigger data-testid="select-course-platform"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PLATFORM_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {form.platform === "prive" && (
                <Field label="Client (optionnel)" full>
                  <Select value={form.client_id || "none"} onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="select-course-client"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.prenom} {c.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
              <Field label="Heure début">
                <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="input-course-start" />
              </Field>
              <Field label="Heure fin">
                <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="input-course-end" />
              </Field>
              <Field label="Départ" full>
                <Input value={form.pickup} onChange={(e) => setForm({ ...form, pickup: e.target.value })} placeholder="Adresse ou zone" data-testid="input-course-pickup" />
              </Field>
              <Field label="Arrivée" full>
                <Input value={form.dropoff} onChange={(e) => setForm({ ...form, dropoff: e.target.value })} placeholder="Adresse ou zone" data-testid="input-course-dropoff" />
              </Field>
              <Field label="Distance (km)">
                <Input type="number" step="0.1" inputMode="decimal" value={form.distance_km} onChange={(e) => setForm({ ...form, distance_km: e.target.value })} data-testid="input-course-distance" />
              </Field>
              <Field label="Durée (min)">
                <Input type="number" inputMode="numeric" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} data-testid="input-course-duration" />
              </Field>
              <Field label="Montant brut (€)">
                <Input type="number" step="0.01" inputMode="decimal" value={form.gross_amount} onChange={(e) => setForm({ ...form, gross_amount: e.target.value })} required data-testid="input-course-gross" />
              </Field>
              <Field label="Commission plateforme (€)">
                <Input type="number" step="0.01" inputMode="decimal" value={form.commission} onChange={(e) => setForm({ ...form, commission: e.target.value })} data-testid="input-course-commission" />
              </Field>
              <Field label="Péages (€)">
                <Input type="number" step="0.01" inputMode="decimal" value={form.tolls} onChange={(e) => setForm({ ...form, tolls: e.target.value })} data-testid="input-course-tolls" />
              </Field>
              <Field label="Pourboire (€)">
                <Input type="number" step="0.01" inputMode="decimal" value={form.tips} onChange={(e) => setForm({ ...form, tips: e.target.value })} data-testid="input-course-tips" />
              </Field>
              <Field label="Commentaires" full>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="input-course-notes" />
              </Field>
              <DialogFooter className="md:col-span-2">
                <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="btn-cancel-course">Annuler</Button>
                <Button type="submit" disabled={saving} data-testid="btn-save-course">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* List */}
      <div className="card-surface overflow-hidden">
        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : items.length === 0 ? (
          <EmptyCourses onAdd={() => setOpen(true)} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Plateforme</th>
                  <th className="text-left px-4 py-3">Trajet</th>
                  <th className="text-right px-4 py-3">Distance</th>
                  <th className="text-right px-4 py-3">Durée</th>
                  <th className="text-right px-4 py-3">CA net</th>
                  <th className="text-right px-4 py-3">€/h</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody data-testid="courses-tbody">
                {items.map((t) => (
                  <tr key={t.id} className="border-b border-border/60 hover:bg-muted/40 transition-colors">
                    <td className="px-4 py-3 num">{t.date}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="border-primary/30">{PLATFORM_LABELS[t.platform] || t.platform}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        <span className="truncate max-w-[220px]">{t.pickup || "—"} → {t.dropoff || "—"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right num">{t.distance_km} km</td>
                    <td className="px-4 py-3 text-right num">{t.duration_min} min</td>
                    <td className="px-4 py-3 text-right num font-semibold">{fmtEuro(t.net_amount)}</td>
                    <td className="px-4 py-3 text-right num text-primary">{fmtEuro(t.hourly_rate)}</td>
                    <td className="px-2 py-3 text-right">
                      <button
                        onClick={() => remove(t.id)}
                        className="text-muted-foreground hover:text-destructive p-1.5 rounded-md hover:bg-destructive/10 transition-colors"
                        data-testid={`btn-delete-course-${t.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={`space-y-1.5 ${full ? "md:col-span-2" : ""}`}>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

function EmptyCourses({ onAdd }) {
  return (
    <div className="border-dashed border-2 border-border/50 m-4 rounded-xl p-10 text-center">
      <Car className="h-10 w-10 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
      <div className="font-manrope font-semibold text-lg mb-1">Aucune course enregistrée</div>
      <div className="text-sm text-muted-foreground mb-6">Commencez par ajouter votre première course du week-end.</div>
      <Button onClick={onAdd} data-testid="btn-add-course-empty">
        <Plus className="h-4 w-4 mr-2" /> Enregistrer une course
      </Button>
    </div>
  );
}
